import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import { Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const DAYS = [
  ["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"],
  ["fri", "Fri"], ["sat", "Sat"], ["sun", "Sun"],
];

const DEFAULT_SCHEDULE = {
  mon: { open: true, start: "09:00", end: "18:00" },
  tue: { open: true, start: "09:00", end: "18:00" },
  wed: { open: true, start: "09:00", end: "18:00" },
  thu: { open: true, start: "09:00", end: "18:00" },
  fri: { open: true, start: "09:00", end: "18:00" },
  sat: { open: true, start: "10:00", end: "17:00" },
  sun: { open: false, start: "00:00", end: "00:00" },
};

const blank = () => ({
  name: "", email: "", phone: "", color: "#1A3626", active: true,
  max_parallel_bookings: 2,
  schedule: { ...DEFAULT_SCHEDULE },
});

export default function Stylists() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());

  const fetchAll = async () => {
    const { data } = await api.get("/stylists");
    setList(data);
  };
  useEffect(() => { fetchAll(); }, []);

  const submit = async () => {
    try {
      if (editing) await api.put(`/stylists/${editing.id}`, form);
      else await api.post("/stylists", form);
      toast.success(editing ? "Stylist updated" : "Stylist created");
      setOpen(false); setEditing(null); setForm(blank());
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete stylist ${s.name}?`)) return;
    try {
      await api.delete(`/stylists/${s.id}`);
      toast.success("Stylist deleted");
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const startEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name, email: s.email || "", phone: s.phone || "",
      color: s.color || "#1A3626", active: s.active,
      max_parallel_bookings: s.max_parallel_bookings ?? 2,
      schedule: s.schedule || { ...DEFAULT_SCHEDULE },
    });
    setOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline">Team</div>
          <h1 className="font-heading text-4xl tracking-tight font-light mt-2">Stylists</h1>
        </div>
        {isAdmin && (
          <Button onClick={() => { setEditing(null); setForm(blank()); setOpen(true); }} className="bg-primary hover:bg-[#2B4F39]" data-testid="stylists-new-btn">
            <Plus size={16} className="mr-1" /> New stylist
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="stylists-grid">
        {list.map((s) => (
          <div key={s.id} className="surface-card surface-card-hover p-6" data-testid={`stylist-card-${s.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-md flex items-center justify-center text-primary-foreground font-medium" style={{ background: s.color }}>
                  {s.name.charAt(0)}
                </div>
                <div>
                  <div className="font-heading text-lg">{s.name}</div>
                  <div className="text-xs text-muted-foreground">{s.email || s.phone || "—"}</div>
                </div>
              </div>
              <span className={`text-xs px-2 py-1 rounded-md ${s.active ? "bg-[#D1E0D7] text-[#1A3626]" : "bg-[#EADCD9] text-[#C46B54]"}`}>
                {s.active ? "Active" : "Off"}
              </span>
            </div>
            <div className="mt-4 grid grid-cols-7 gap-1 text-[10px] font-mono">
              {DAYS.map(([k, label]) => (
                <div key={k} className={`px-1 py-1.5 rounded text-center ${s.schedule?.[k]?.open ? "bg-secondary text-foreground" : "bg-[#EADCD9]/40 text-muted-foreground"}`}>
                  <div>{label}</div>
                  <div className="text-[9px]">{s.schedule?.[k]?.open ? `${s.schedule[k].start}` : "—"}</div>
                </div>
              ))}
            </div>
            <div
              className="mt-3 flex items-center justify-between text-xs text-muted-foreground"
              data-testid={`stylist-capacity-${s.id}`}
            >
              <span className="overline">Capacity</span>
              <span className="font-mono tabular-nums text-foreground">
                {s.max_parallel_bookings ?? 2} overlapping appointment{(s.max_parallel_bookings ?? 2) === 1 ? "" : "s"}
              </span>
            </div>
            {isAdmin && (
              <div className="mt-4 flex gap-2">
                <Button variant="outline" size="sm" className="flex-1" onClick={() => startEdit(s)} data-testid={`stylist-edit-${s.id}`}>Edit</Button>
                <Button variant="ghost" size="sm" onClick={() => remove(s)} data-testid={`stylist-delete-${s.id}`} className="text-destructive">
                  <Trash size={14} />
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-2xl" data-testid="stylist-form-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight">{editing ? "Edit stylist" : "New stylist"}</DialogTitle>
            <DialogDescription>Profile and weekly working hours.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label className="overline">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#F9F8F6]" data-testid="stylist-name-input" /></div>
              <div className="space-y-2"><Label className="overline">Color</Label><Input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="bg-[#F9F8F6] h-10" data-testid="stylist-color-input" /></div>
              <div className="space-y-2"><Label className="overline">Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#F9F8F6]" /></div>
              <div className="space-y-2"><Label className="overline">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#F9F8F6]" /></div>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="stylist-active-switch" />
              <Label className="overline">Active</Label>
            </div>

            <div className="space-y-2" data-testid="stylist-capacity-field">
              <Label className="overline">Maximum simultaneous clients</Label>
              <div className="flex gap-2" role="radiogroup" aria-label="Maximum simultaneous clients">
                {[1, 2, 3].map((n) => {
                  const selected = (form.max_parallel_bookings ?? 2) === n;
                  return (
                    <button
                      key={n}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => setForm({ ...form, max_parallel_bookings: n })}
                      data-testid={`stylist-capacity-option-${n}`}
                      className={`flex-1 rounded-md border px-4 py-2.5 text-sm font-medium transition-all ${
                        selected
                          ? "bg-primary text-primary-foreground border-primary shadow-sm"
                          : "bg-[#F9F8F6] hover:border-primary/40"
                      }`}
                    >
                      <div className="font-mono text-lg tabular-nums">{n}</div>
                      <div className="text-[10px] uppercase tracking-wide opacity-70 mt-0.5">
                        {n === 1 ? "Single appointment" : n === 2 ? "2 overlapping" : "3 overlapping"}
                      </div>
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                Allows overlapping customer journeys during processing stages such as colour development.
                Occupied working stages still cannot overlap — the stylist can only physically work on
                one client at a time.
              </p>
              {form.max_parallel_bookings === 3 && (
                <div
                  className="rounded-md border border-[#C46B54]/40 bg-[#EADCD9]/30 px-3 py-2 text-[11px] text-[#7A3A2A] leading-relaxed"
                  data-testid="stylist-capacity-warning"
                  role="alert"
                >
                  Best suited to stylists managing multiple colour or treatment processing windows
                  simultaneously. Overlap is only possible while other clients are in a processing stage.
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label className="overline">Weekly schedule</Label>
              <div className="space-y-2">
                {DAYS.map(([k, label]) => {
                  const day = form.schedule[k];
                  return (
                    <div key={k} className="flex items-center gap-2">
                      <div className="w-12 text-sm font-medium">{label}</div>
                      <Switch
                        checked={day.open}
                        onCheckedChange={(v) => setForm({ ...form, schedule: { ...form.schedule, [k]: { ...day, open: v } } })}
                        data-testid={`schedule-${k}-switch`}
                      />
                      <Input
                        type="time"
                        value={day.start}
                        disabled={!day.open}
                        onChange={(e) => setForm({ ...form, schedule: { ...form.schedule, [k]: { ...day, start: e.target.value } } })}
                        className="bg-[#F9F8F6] flex-1"
                      />
                      <span className="text-muted-foreground">→</span>
                      <Input
                        type="time"
                        value={day.end}
                        disabled={!day.open}
                        onChange={(e) => setForm({ ...form, schedule: { ...form.schedule, [k]: { ...day, end: e.target.value } } })}
                        className="bg-[#F9F8F6] flex-1"
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} className="bg-primary hover:bg-[#2B4F39]" data-testid="stylist-submit-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
